import { useResolveGoalParentChanges } from 'features/goals';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import { TreeSectionHeader } from 'shared/components/ui/tree-section-header';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  TreeItemMove,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useNoGroupingGoalListTree } from '../../hooks/use-no-grouping-goal-list-tree';
import { GroupingGoalListProps } from '../../types';
import { TreeGoalCard } from '../tree-goal-card';
import * as Styled from './no-grouping-goal-list.style';

const droppedTypes = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.GoalDeadline]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

const dragTypes = [TaskListItemTypes.Goal];
// No reorder handler (e.g. the life-area Projects list) → dragging is disabled
// so cards can't be dragged only to snap back.
const noDragTypes: TaskListItemTypes[] = [];

export const NoGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  goalChildrenByParentId,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  isMultiSelectModifierPressed,
  onUpdateProgress,
  hideLifeArea,
  hideArchived,
  hideCompleted,
  rootLink,
  showParentGoalLabel,
  showParentLifeAreaLabel,
  showParentLabelsForGoals,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();

  const goalsMap = useMemo(
    () =>
      goals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {}),
    [goals],
  );

  const resolveGoalParentChanges = useResolveGoalParentChanges();

  const treeItems = useNoGroupingGoalListTree(goals, {
    hideArchived,
    hideCompleted,
    goalChildrenByParentId,
    rootLink,
  });

  // Nesting and un-nesting move one parent link; the frozen legacy chain is never rewritten here.
  const _onReorder = (updatedItems: TreeItem[], moves: TreeItemMove[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: UpdateGoal[] = [];
    const updatedSections: { id: ID; goalSorting: ID[] }[] = [];
    const goalParentChanges = resolveGoalParentChanges(
      moves,
      [rootId, ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID],
      rootLink?.goalId,
    );

    updatedItems.forEach((item) => {
      if (item.type === TaskListItemTypes.Goal) {
        if (!goalsMap[item.id]) {
          return;
        }
        updatedDocs.push({ id: item.id, goalSorting: item.children ?? [] });
        return;
      }

      if (
        [rootId, ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID].includes(item.id)
      ) {
        updatedSections.push({ id: item.id, goalSorting: item.children ?? [] });
      }
    });

    onReorder(updatedSections, updatedDocs, undefined, goalParentChanges);
  };

  const { close, position, payload } = useContextMenu();

  const renderItem = useCallback(
    ({ itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = goals.find((g) => g.id === itemId);
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            rootLink={rootLink}
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            hideLifeArea={hideLifeArea}
            showParentGoalLabel={showParentGoalLabel}
            showParentLifeAreaLabel={showParentLifeAreaLabel}
            showParentLabelsForGoals={showParentLabelsForGoals}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (itemData.type === TaskListItemTypes.LifeArea) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;

        if (itemId === COMPLETED_SECTION_ID) {
          return (
            <TreeSectionHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.completed.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }
        if (itemId === ARCHIVED_SECTION_ID) {
          return (
            <TreeSectionHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.archived.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }
      }

      return null;
    },
    [
      treeItems,
      rootLink,
      goals,
      onUpdateProgress,
      hideLifeArea,
      t,
      showParentGoalLabel,
      showParentLifeAreaLabel,
      showParentLabelsForGoals,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={onReorder ? _onReorder : undefined}
        dropTypes={droppedTypes}
        dragTypes={onReorder ? dragTypes : noDragTypes}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
